/**
 * V3 — Page Audit & Sécurité (CEO admin only).
 *
 * 3 onglets :
 *   1. Activité récente : derniers events legacy_sync_audit (filtres action + key)
 *   2. Soft-deleted     : clés supprimées récupérables (preview + restore + purge)
 *   3. Stats            : compteurs globaux, top users, breakdown actions
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useV3Auth } from './auth';
import {
  auditRecent, auditDeleted, auditDeletedValue,
  auditRestore, auditPurge, auditStats, auditExportCsvUrl, auditRescueStatus,
} from './api';
import { ShieldAlert, RotateCcw, Trash2, Eye, RefreshCw, AlertTriangle, Activity, Archive, BarChart3, Download, LifeBuoy, Check, X as XIcon } from 'lucide-react';

const ACTION_COLORS = {
  create:  { bg: '#064e3b', fg: '#6ee7b7', label: 'CREATE' },
  update:  { bg: '#1e3a8a', fg: '#93c5fd', label: 'UPDATE' },
  delete:  { bg: '#7f1d1d', fg: '#fecaca', label: 'DELETE' },
  restore: { bg: '#4c1d95', fg: '#ddd6fe', label: 'RESTORE' },
  purge:   { bg: '#451a03', fg: '#fdba74', label: 'PURGE' },
};

function ActionPill({ action }) {
  const c = ACTION_COLORS[action] || { bg: '#1e293b', fg: '#cbd5e1', label: action.toUpperCase() };
  return (
    <span style={{
      background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
    }}>{c.label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' });
  } catch (_) { return iso; }
}

function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return n + 'B';
  if (n < 1024*1024) return (n / 1024).toFixed(1) + 'kB';
  return (n / 1024 / 1024).toFixed(1) + 'MB';
}

// ============================================================
// Onglet 1 : Activité récente
// ============================================================
function TabRecent() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filterAction, setFilterAction] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await auditRecent({ limit: 200, action: filterAction, key_search: filterKey });
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [filterAction, filterKey]);

  useEffect(() => { Promise.resolve().then(() => load()); }, [load]);

  return (
    <div data-testid="audit-tab-recent">
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={filterAction}
          onChange={e => setFilterAction(e.target.value)}
          data-testid="audit-filter-action"
          style={{ background: '#020617', border: '1px solid #1e293b', color: '#e2e8f0', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}
        >
          <option value="">Toutes les actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="restore">Restore</option>
          <option value="purge">Purge</option>
        </select>
        <input
          type="text"
          placeholder="🔍 Filtrer par clé (substring)…"
          value={filterKey}
          onChange={e => setFilterKey(e.target.value)}
          data-testid="audit-filter-key"
          style={{ flex: 1, minWidth: 240, background: '#020617', border: '1px solid #1e293b', color: '#e2e8f0', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}
        />
        <button
          onClick={load}
          data-testid="audit-refresh-recent"
          style={{ background: '#1e293b', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <RefreshCw size={14} /> Actualiser
        </button>
        <a
          href={auditExportCsvUrl({ action: filterAction, key_search: filterKey, limit: 1000 })}
          data-testid="audit-export-csv"
          title="Télécharger le journal d'audit (max 1000 lignes) avec les filtres actuels"
          style={{ background: '#15803d', color: '#fff', textDecoration: 'none', padding: '8px 14px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}
        >
          <Download size={14} /> Export CSV
        </a>
        <span style={{ color: '#64748b', fontSize: 12 }}>{total} évènements</span>
      </div>

      {err && <div className="v3-error" data-testid="audit-error">⚠️ {err}</div>}
      {loading && <div style={{ color: '#64748b', padding: 20 }}>Chargement…</div>}

      {!loading && items.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Aucun évènement.</div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#020617', color: '#94a3b8', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Quand</th>
                <th style={{ padding: '10px 12px' }}>Action</th>
                <th style={{ padding: '10px 12px' }}>Clé</th>
                <th style={{ padding: '10px 12px' }}>Auteur</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Δ taille</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <React.Fragment key={idx}>
                  <tr data-testid={`audit-row-${idx}`} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: '8px 12px', color: '#cbd5e1', whiteSpace: 'nowrap' }}>{fmtDate(it.ts_iso)}</td>
                    <td style={{ padding: '8px 12px' }}><ActionPill action={it.action} /></td>
                    <td style={{ padding: '8px 12px', color: '#e2e8f0', fontFamily: 'monospace', fontSize: 12 }}>{it.key}</td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{it.user || '?'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontSize: 11 }}>
                      {fmtBytes(it.before_len)} → {fmtBytes(it.after_len)}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <button
                        onClick={() => setExpanded(expanded === idx ? null : idx)}
                        data-testid={`audit-diff-${idx}`}
                        style={{ background: '#1e293b', color: '#94a3b8', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                      >
                        {expanded === idx ? 'Masquer' : 'Diff'}
                      </button>
                    </td>
                  </tr>
                  {expanded === idx && (
                    <tr style={{ background: '#020617' }}>
                      <td colSpan={6} style={{ padding: 16 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <div style={{ color: '#fca5a5', fontWeight: 600, marginBottom: 6, fontSize: 11, letterSpacing: 1 }}>AVANT</div>
                            <pre style={{ background: '#0b1220', padding: 12, borderRadius: 6, color: '#fecaca', fontSize: 11, maxHeight: 240, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                              {it.before || <em style={{ color: '#64748b' }}>∅ (création ou null)</em>}
                            </pre>
                          </div>
                          <div>
                            <div style={{ color: '#86efac', fontWeight: 600, marginBottom: 6, fontSize: 11, letterSpacing: 1 }}>APRÈS</div>
                            <pre style={{ background: '#0b1220', padding: 12, borderRadius: 6, color: '#bbf7d0', fontSize: 11, maxHeight: 240, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                              {it.after || <em style={{ color: '#64748b' }}>∅ (suppression)</em>}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Onglet 2 : Soft-deleted
// ============================================================
function TabDeleted() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await auditDeleted();
      setItems(r.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { Promise.resolve().then(() => load()); }, [load]);

  const onPreview = async (key) => {
    setBusyKey(key);
    try {
      const r = await auditDeletedValue(key);
      setPreview({ key, value: r.value || '', deleted_by: r.deleted_by, deleted_at_ms: r.deleted_at_ms });
    } catch (e) {
      alert('Erreur preview: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusyKey(''); }
  };

  const onRestore = async (key) => {
    if (!window.confirm(`Restaurer la clé "${key}" ?\n\nElle réapparaîtra dans l'app legacy au prochain refresh.`)) return;
    setBusyKey(key);
    try {
      await auditRestore(key);
      setPreview(null);
      await load();
    } catch (e) {
      alert('Erreur restore: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusyKey(''); }
  };

  const onPurge = async (key) => {
    if (!window.confirm(`⚠️ SUPPRESSION DÉFINITIVE de "${key}".\n\nCette action est IRRÉVERSIBLE. Continuer ?`)) return;
    if (!window.confirm(`Êtes-vous absolument sûr ?\n\nTaper OK pour confirmer le PURGE de:\n${key}`)) return;
    setBusyKey(key);
    try {
      await auditPurge(key);
      setPreview(null);
      await load();
    } catch (e) {
      alert('Erreur purge: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusyKey(''); }
  };

  return (
    <div data-testid="audit-tab-deleted">
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <button
          onClick={load}
          data-testid="audit-refresh-deleted"
          style={{ background: '#1e293b', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <RefreshCw size={14} /> Actualiser
        </button>
        <span style={{ color: '#64748b', fontSize: 12 }}>{items.length} clés soft-deleted</span>
      </div>

      {err && <div className="v3-error">⚠️ {err}</div>}
      {loading && <div style={{ color: '#64748b', padding: 20 }}>Chargement…</div>}

      {!loading && items.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b', background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8 }}>
          ✅ Aucune clé soft-deleted. Tout est OK.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#020617', color: '#94a3b8', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Clé</th>
                <th style={{ padding: '10px 12px' }}>Supprimée par</th>
                <th style={{ padding: '10px 12px' }}>Quand</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.key} data-testid={`audit-deleted-row-${idx}`} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '8px 12px', color: '#e2e8f0', fontFamily: 'monospace', fontSize: 12 }}>{it.key}</td>
                  <td style={{ padding: '8px 12px', color: '#fca5a5' }}>{it.deleted_by || '?'}</td>
                  <td style={{ padding: '8px 12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {it.deleted_at_ms ? fmtDate(new Date(it.deleted_at_ms).toISOString()) : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => onPreview(it.key)} disabled={busyKey === it.key}
                      data-testid={`audit-preview-${idx}`}
                      style={{ background: '#1e293b', color: '#94a3b8', border: 'none', padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Eye size={12} /> Voir
                    </button>
                    <button onClick={() => onRestore(it.key)} disabled={busyKey === it.key}
                      data-testid={`audit-restore-${idx}`}
                      style={{ background: '#15803d', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <RotateCcw size={12} /> Restaurer
                    </button>
                    <button onClick={() => onPurge(it.key)} disabled={busyKey === it.key}
                      data-testid={`audit-purge-${idx}`}
                      style={{ background: '#7f1d1d', color: '#fecaca', border: 'none', padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Trash2 size={12} /> Purger
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setPreview(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fbbf24', fontWeight: 700, fontFamily: 'monospace' }}>{preview.key}</div>
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>Supprimée par {preview.deleted_by} le {preview.deleted_at_ms ? fmtDate(new Date(preview.deleted_at_ms).toISOString()) : '—'}</div>
              </div>
              <button onClick={() => setPreview(null)} style={{ background: '#1e293b', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>Fermer</button>
            </div>
            <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
              <pre style={{ background: '#020617', padding: 12, borderRadius: 6, color: '#cbd5e1', fontSize: 11, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{preview.value}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Onglet 3 : Stats
// ============================================================
function TabStats() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.resolve().then(async () => {
      try {
        const r = await auditStats();
        setStats(r);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    });
  }, []);

  if (err) return <div className="v3-error">⚠️ {err}</div>;
  if (!stats) return <div style={{ color: '#64748b', padding: 20 }}>Chargement…</div>;

  return (
    <div data-testid="audit-tab-stats">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 22 }}>
        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 11, color: '#64748b', letterSpacing: 1, fontWeight: 600 }}>ÉVÈNEMENTS TOTAUX</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#e2e8f0', marginTop: 8 }}>{stats.total_events?.toLocaleString('fr-FR') || 0}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Dans legacy_sync_audit</div>
        </div>
        <div style={{ background: '#0b1220', border: '1px solid #7f1d1d', borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 11, color: '#fca5a5', letterSpacing: 1, fontWeight: 600 }}>SOFT-DELETED ACTIFS</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#fecaca', marginTop: 8 }}>{stats.soft_deleted_count || 0}</div>
          <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 4 }}>Clés récupérables</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, marginBottom: 12 }}>📊 Répartition par action</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(stats.by_action || {}).sort((a,b) => b[1] - a[1]).map(([action, count]) => {
              const pct = stats.total_events > 0 ? Math.round(100 * count / stats.total_events) : 0;
              return (
                <div key={action} data-testid={`stats-action-${action}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <ActionPill action={action} />
                    <span style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 600 }}>{count} ({pct}%)</span>
                  </div>
                  <div style={{ height: 6, background: '#1e293b', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', background: ACTION_COLORS[action]?.fg || '#94a3b8' }}></div>
                  </div>
                </div>
              );
            })}
            {Object.keys(stats.by_action || {}).length === 0 && <span style={{ color: '#64748b', fontSize: 12 }}>Aucune action enregistrée.</span>}
          </div>
        </div>

        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, marginBottom: 12 }}>👥 Top 10 contributeurs</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(stats.top_users || []).map((u, idx) => (
              <div key={idx} data-testid={`stats-topuser-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: idx < stats.top_users.length - 1 ? '1px solid #1e293b' : 'none' }}>
                <span style={{ color: '#cbd5e1', fontSize: 13 }}>
                  <span style={{ color: '#fbbf24', marginRight: 6, fontWeight: 700 }}>#{idx + 1}</span>
                  {u.user || '?'}
                </span>
                <span style={{ color: '#22c55e', fontSize: 13, fontWeight: 600 }}>{u.count}</span>
              </div>
            ))}
            {(stats.top_users || []).length === 0 && <span style={{ color: '#64748b', fontSize: 12 }}>Aucun contributeur enregistré.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Onglet 4 : Rescue Équipe
// ============================================================
function TabRescue() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await auditRescueStatus();
      setData(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { Promise.resolve().then(() => load()); }, [load]);

  if (err) return <div className="v3-error">⚠️ {err}</div>;
  if (loading || !data) return <div style={{ color: '#64748b', padding: 20 }}>Chargement…</div>;

  const completed = (data.rescued_count / (data.expected_users.length || 1)) * 100;

  return (
    <div data-testid="audit-tab-rescue">
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={load}
          data-testid="audit-refresh-rescue"
          style={{ background: '#1e293b', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <RefreshCw size={14} /> Actualiser
        </button>
        <span style={{ color: '#64748b', fontSize: 12 }}>Lien à envoyer : <code style={{ background: '#020617', padding: '2px 6px', borderRadius: 4, color: '#fbbf24' }}>grouper-ops.com/rescue.html</code></span>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 10, color: '#64748b', letterSpacing: 1, fontWeight: 700 }}>UTILISATEURS RESCUÉS</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e', marginTop: 6 }}>
            {data.rescued_count} <span style={{ color: '#64748b', fontSize: 13 }}>/ {data.expected_users.length}</span>
          </div>
          <div style={{ height: 6, background: '#1e293b', borderRadius: 3, marginTop: 6, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: completed + '%', background: '#22c55e' }}></div>
          </div>
        </div>
        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 10, color: '#64748b', letterSpacing: 1, fontWeight: 700 }}>CLÉS PUSHÉES ✅</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginTop: 6 }}>{data.total_keys_pushed}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Sauvegardées au cloud</div>
        </div>
        <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 10, color: '#fca5a5', letterSpacing: 1, fontWeight: 700 }}>ÉCHECS</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: data.total_keys_failed > 0 ? '#fca5a5' : '#64748b', marginTop: 6 }}>{data.total_keys_failed}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Push refusés/erreur</div>
        </div>
      </div>

      {/* Missing users */}
      {data.missing_users && data.missing_users.length > 0 && (
        <div style={{ background: '#7c2d12', border: '1px solid #f59e0b', borderRadius: 8, padding: 14, marginBottom: 18 }} data-testid="audit-missing-users">
          <div style={{ fontSize: 12, fontWeight: 700, color: '#fbbf24', marginBottom: 8 }}>
            ⚠️ {data.missing_users.length} utilisateur(s) n&apos;ont PAS encore fait le rescue
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.missing_users.map(u => (
              <span key={u} style={{ background: '#451a03', color: '#fde68a', padding: '4px 10px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>
                <XIcon size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {u}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 10 }}>
            👉 Relance-les via WhatsApp avec le lien : <strong>grouper-ops.com/rescue.html</strong>
          </div>
        </div>
      )}

      {/* Detail per user */}
      <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', background: '#020617', borderBottom: '1px solid #1e293b', fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>
          Détails par utilisateur ({data.by_user.length} rescue(s) enregistré(s))
        </div>
        {data.by_user.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>
            Aucun rescue exécuté pour le moment.<br/>
            <span style={{ fontSize: 12 }}>Demande à ton équipe d&apos;ouvrir <strong>grouper-ops.com/rescue.html</strong></span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#020617', color: '#94a3b8', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Utilisateur</th>
                <th style={{ padding: '10px 12px' }}>Quand</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Clés push</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Échecs</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Stockage</th>
                <th style={{ padding: '10px 12px' }}>Appareil</th>
              </tr>
            </thead>
            <tbody>
              {data.by_user.map((u, idx) => (
                <tr key={idx} data-testid={`audit-rescue-row-${idx}`} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '8px 12px', color: '#e2e8f0', fontWeight: 600 }}>
                    <Check size={12} color="#22c55e" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    {u.user}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {u.ts_iso ? new Date(u.ts_iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#86efac', fontWeight: 700 }}>{u.pushed}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: u.failed > 0 ? '#fca5a5' : '#64748b' }}>{u.failed}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#cbd5e1' }}>{u.storage_mo} Mo</td>
                  <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.user_agent}>
                    {u.user_agent.includes('iPhone') ? '📱 iPhone' :
                     u.user_agent.includes('Android') ? '📱 Android' :
                     u.user_agent.includes('Edge') ? '🖥 Edge' :
                     u.user_agent.includes('Chrome') ? '🖥 Chrome' : '🖥 Autre'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Composant principal
// ============================================================
export default function V3Audit() {
  const { user } = useV3Auth();
  const [tab, setTab] = useState('recent');

  if (user && user.role !== 'admin') {
    return (
      <div style={{ padding: 40, textAlign: 'center' }} data-testid="audit-no-access">
        <AlertTriangle size={48} color="#f59e0b" style={{ margin: '0 auto 16px' }} />
        <div style={{ fontSize: 18, fontWeight: 700, color: '#fbbf24' }}>Accès restreint</div>
        <div style={{ color: '#94a3b8', marginTop: 8 }}>Cette page est réservée au CEO (role admin).</div>
      </div>
    );
  }

  const tabs = [
    { id: 'recent',  label: 'Activité récente', icon: Activity },
    { id: 'deleted', label: 'Soft-deleted',     icon: Archive },
    { id: 'stats',   label: 'Statistiques',     icon: BarChart3 },
    { id: 'rescue',  label: 'Rescue Équipe',    icon: LifeBuoy },
  ];

  return (
    <div data-testid="v3-audit-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <ShieldAlert size={28} color="#fbbf24" />
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Audit & Sécurité</h1>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>Traçabilité complète des modifications legacy_sync — CEO uniquement</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid #1e293b' }} role="tablist">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`audit-tab-${t.id}-btn`}
              role="tab"
              aria-selected={active}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '10px 18px', color: active ? '#fbbf24' : '#94a3b8',
                fontWeight: active ? 700 : 500, fontSize: 14,
                borderBottom: active ? '2px solid #fbbf24' : '2px solid transparent',
                marginBottom: -1, display: 'flex', alignItems: 'center', gap: 8,
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'recent'  && <TabRecent />}
      {tab === 'deleted' && <TabDeleted />}
      {tab === 'stats'   && <TabStats />}
      {tab === 'rescue'  && <TabRescue />}
    </div>
  );
}
